"use client";
import { cva, type VariantProps } from "class-variance-authority";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import { ClaimWrapper } from "./claim-wrapper";
import "./claim.css";
import { useGSAP } from "@gsap/react";
import type React from "react";
import { useRef } from "react";
import type { ClaimInspectorProps } from "./claim.preview";

gsap.registerPlugin(ScrollTrigger, useGSAP);

const claimVariants = cva("", {
  variants: {
    backgroundColor: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
  },
  defaultVariants: {
    backgroundColor: "white",
  },
});

export interface ClaimProps extends VariantProps<typeof claimVariants> {
  sys?: {
    id?: string;
    locale?: string;
  };
  fullWidth?: boolean;
  backgroundColor?: "black" | "white" | "whitesmoke";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  text?: string;
  className?: string;
  showMarkers?: boolean;
  offsetLeft?: number;
  inspectorProps?: ClaimInspectorProps;
}

export function Claim({ backgroundColor, ...props }: Readonly<ClaimProps>) {
  const container = useRef<HTMLQuoteElement>(null);

  useGSAP(
    () => {
      initGsapAnimation({
        fullWidth: props?.fullWidth,
        showMarkers: props?.showMarkers,
        text: props?.text,
        offsetLeft: props?.offsetLeft,
        ref: container,
      });
    },
    {
      dependencies: [
        props.fullWidth,
        props.showMarkers,
        props.text,
        props.offsetLeft,
      ],
      scope: container,
      revertOnUpdate: true,
    }
  );

  const variantClasses = claimVariants({
    backgroundColor,
  });
  const color = backgroundColor === "black" ? "white" : "black";
  return (
    <Container
      className={cn(
        variantClasses,
        "component-claim overflow-hidden",
        props.className
      )}
      fullWidth={true}
      outerContainerOnly={props.fullWidth ?? false}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      test-id="claim-component"
      {...props.inspectorProps?.container}
    >
      {props.fullWidth ? (
        <ClaimWrapper color={color} ref={container} text={props.text} />
      ) : (
        <Row className={"overflow-hidden"}>
          <Column className="col-span-12">
            <ClaimWrapper color={color} ref={container} text={props.text} />
          </Column>
        </Row>
      )}
    </Container>
  );
}

/**
 * Initializes GSAP animation for the claim component.
 * @param props - Configuration properties for the animation.
 */
const initGsapAnimation = (props: {
  fullWidth?: boolean;
  showMarkers?: boolean;
  text?: string;
  offsetLeft?: number;
  ref: React.RefObject<HTMLQuoteElement | null>;
}) => {
  const claim = props.ref.current;
  const wrapper = props.ref.current?.parentNode as HTMLElement;

  // Guard clause: ensure elements exist before proceeding
  if (!(wrapper && claim)) {
    return;
  }

  const FULL_PERCENT = 100;
  const OFFSET_LEFT = props.offsetLeft || 0;

  // Use functions so these are recalculated each time ScrollTrigger refreshes
  const calculateStart = () => {
    const wrapperWidth = wrapper.clientWidth;
    return OFFSET_LEFT > 1
      ? Math.max(FULL_PERCENT, (wrapperWidth * OFFSET_LEFT) / FULL_PERCENT)
      : 0;
  };

  const calculateEnd = () =>
    Math.min(0, wrapper.clientWidth - claim.scrollWidth);

  // Initial setup
  const startValue = calculateStart();
  claim.style.setProperty("--OFFSET_LEFT", `${startValue}px`);

  // Create the animation with function-based values that get recalculated
  return gsap.fromTo(
    claim,
    {
      "--translateX": () => `${calculateStart()}px`,
    },
    {
      "--translateX": () => `${calculateEnd()}px`,
      scrollTrigger: {
        trigger: claim,
        scrub: 1,
        markers: props.showMarkers,
        start: "top 80%",
        end: "top 10%",
        invalidateOnRefresh: true,
        onRefresh: () => {
          // Recalculate and update CSS variable
          const newStart = calculateStart();
          claim.style.setProperty("--OFFSET_LEFT", `${newStart}px`);
        },
      },
    }
  );
};
